import { GraphQLEnums } from "~/src/__generated__/GraphQLEnums";
import { POPUP_GROUP_DISTRIBUTION_LABELS } from "~/src/components/features/popup/PopupGroupForm/popupGroupDistributionLabels";

/** Column groups for the popup import horizontal table (see README). */

const popupImportYdaTriggerSelectOptions = [
  { label: "未設定", value: "" },
  ...GraphQLEnums.PopupYdaTrigger,
];

export type PopupImportColumnGroupId =
  | "row_lead"
  | "funnel_page"
  | "popup_group"
  | "popup"
  | "popup_images"
  | "popup_group_filters";

export const POPUP_IMPORT_COLUMN_GROUP_IDS: PopupImportColumnGroupId[] = [
  "row_lead",
  "funnel_page",
  "popup_group",
  "popup_group_filters",
  "popup",
  "popup_images",
];

/** Column groups that appear in the “show / hide” toolbar (`row_lead` is always visible). */
export const POPUP_IMPORT_TOGGLEABLE_GROUP_IDS: PopupImportColumnGroupId[] =
  POPUP_IMPORT_COLUMN_GROUP_IDS.filter((id) => id !== "row_lead");

/** First thead row: empty string renders blank group header cell. */
export const POPUP_IMPORT_GROUP_LABELS: Record<
  PopupImportColumnGroupId,
  string
> = {
  row_lead: "",
  funnel_page: "ファネル・ページ",
  popup_group: "基本設定：ポップアップグループ",
  popup_group_filters: "基本設定：流入元セグメント",
  popup: "ポップアップパターン",
  popup_images: "ポップアップパターン画像",
};

type PopupImportGroupColorSet = {
  headerBg: string;
  text: string;
};

const POPUP_IMPORT_GROUP_COLORS: Record<
  PopupImportColumnGroupId,
  PopupImportGroupColorSet
> = {
  row_lead: { headerBg: "#EDF2F7", text: "#4A5568" },
  funnel_page: { headerBg: "#E6F1FB", text: "#185FA5" },
  popup_group: { headerBg: "#E1F5EE", text: "#0F6E56" },
  popup: { headerBg: "#FAEEDA", text: "#854F0B" },
  popup_images: { headerBg: "#FAECE7", text: "#993C1D" },
  popup_group_filters: { headerBg: "#EEEDFE", text: "#534AB7" },
};

export function getPopupImportGroupHeaderBg(
  groupId: PopupImportColumnGroupId
): string {
  return POPUP_IMPORT_GROUP_COLORS[groupId].headerBg;
}

export function getPopupImportGroupText(
  groupId: PopupImportColumnGroupId
): string {
  return POPUP_IMPORT_GROUP_COLORS[groupId].text;
}

/** Checkbox labels when `POPUP_IMPORT_GROUP_LABELS` is blank (row_lead). */
export function getPopupImportGroupCheckboxLabel(
  id: PopupImportColumnGroupId
): string {
  const header = POPUP_IMPORT_GROUP_LABELS[id];
  if (header) return header;
  if (id === "row_lead") return "アクション・ID・ステータス";
  return id;
}

export type PopupImportCellKind =
  | "action_select"
  | "funnel_select"
  | "funnel_page_select"
  | "formik_date"
  | "schedule_list"
  | "source_id_list"
  | "deliver_day_of_weeks"
  | "distribution_type_select"
  | "deliver_kind_select"
  | "content_kind_select"
  | "text"
  | "textarea"
  | "readonly"
  | "string_list"
  | "referrer_list"
  | "exclude_referrer_list"
  | "json_array"
  | "url_list"
  | "cookies_cell"
  | "synced_group_name"
  | "switch_bool"
  | "distribution_status_switch"
  | "csv_bool_switch"
  | "image_slot"
  | "enum_select"
  | "display_popup_again_image"
  | "graphql_enum_multi"
  | "action_timing_block";

/** Keys into `GraphQLEnums` for segment filters (same lists as `PopupGroupForm`). */
export type PopupImportGraphqlEnumMultiKey =
  | "Device"
  | "Platform"
  | "Browser"
  | "Gender"
  | "AgeRange"
  | "AnnualIncome";

export type PopupImportSelectOption = { label: string; value: string };

export type PopupImportColumnDef = {
  id: string;
  groupId: PopupImportColumnGroupId;
  headerLabel: string;
  /** Path under `popups[i].` */
  formikPath: string;
  cellKind: PopupImportCellKind;
  minW?: string;
  /** Zero-based image block index (画像N → N-1) for any column in `popup_images`. */
  imageSlot?: number;
  /** Required when `cellKind` is `enum_select` (same option lists as popup pattern form). */
  selectOptions?: ReadonlyArray<PopupImportSelectOption>;
  /** Required when `cellKind` is `graphql_enum_multi`. */
  graphqlEnumMultiKey?: PopupImportGraphqlEnumMultiKey;
};

export const POPUP_IMPORT_IMAGE_SLOT_COUNT = 8;

/** Minimum number of 画像N blocks (and sub-columns) shown in the import review table. */
export const POPUP_IMPORT_MIN_VISIBLE_IMAGE_SLOTS = 2;

/** Default visible image-slot count from optional CSV-derived max (1-based highest 画像N with data). */
export function defaultVisibleImageSlotCount(csvMaxImageSlot?: number): number {
  if (csvMaxImageSlot === undefined) {
    return POPUP_IMPORT_MIN_VISIBLE_IMAGE_SLOTS;
  }
  return Math.min(
    POPUP_IMPORT_IMAGE_SLOT_COUNT,
    Math.max(POPUP_IMPORT_MIN_VISIBLE_IMAGE_SLOTS, csvMaxImageSlot)
  );
}

function imageColumns(): PopupImportColumnDef[] {
  const out: PopupImportColumnDef[] = [];
  for (let slot = 0; slot < POPUP_IMPORT_IMAGE_SLOT_COUNT; slot++) {
    const n = slot + 1;
    out.push(
      {
        id: `img_${n}_slot`,
        groupId: "popup_images",
        headerLabel: `画像${n}`,
        formikPath: `popupImages.${slot}`,
        cellKind: "image_slot",
        imageSlot: slot,
        minW: "220px",
      },
      {
        id: `img_${n}_yda`,
        groupId: "popup_images",
        headerLabel: `画像${n}YDAコード`,
        formikPath: `popupImages.${slot}.honeycombCode`,
        cellKind: "text",
        imageSlot: slot,
        minW: "96px",
      },
      {
        id: `img_${n}_alt`,
        groupId: "popup_images",
        headerLabel: `画像${n}画像が表示されない場合の表記`,
        formikPath: `popupImages.${slot}.alt`,
        cellKind: "text",
        imageSlot: slot,
        minW: "120px",
      },
      {
        id: `img_${n}_link`,
        groupId: "popup_images",
        headerLabel: `画像${n}画像クリック時のURL`,
        formikPath: `popupImages.${slot}.linkUrl`,
        cellKind: "text",
        imageSlot: slot,
        minW: "120px",
      },
      {
        id: `img_${n}_inherit`,
        groupId: "popup_images",
        headerLabel: `画像${n}クエリパラメータを引き継ぐ（※ページの?以降の文字を引き継ぎます）`,
        formikPath: `popupImages.${slot}.isInheritQuery`,
        cellKind: "switch_bool",
        imageSlot: slot,
        minW: "72px",
      }
    );
  }
  return out;
}

const rowLeadColumns: PopupImportColumnDef[] = [
  {
    id: "action",
    groupId: "row_lead",
    headerLabel: "アクション",
    formikPath: "action",
    cellKind: "action_select",
    minW: "140px",
  },
  {
    id: "popup_id",
    groupId: "row_lead",
    headerLabel: "ポップアップID",
    formikPath: "popupId",
    cellKind: "text",
    minW: "120px",
  },
  {
    id: "status",
    groupId: "row_lead",
    headerLabel: "ステータス",
    formikPath: "popup.statusRaw",
    cellKind: "distribution_status_switch",
    minW: "88px",
  },
];

const funnelPageColumns: PopupImportColumnDef[] = [
  {
    id: "verification_funnel",
    groupId: "funnel_page",
    headerLabel: "実施するファネル",
    formikPath: "verificationFunnelId",
    cellKind: "funnel_select",
    minW: "160px",
  },
  {
    id: "verification_page",
    groupId: "funnel_page",
    headerLabel: "実施するページ",
    formikPath: "verificationPageId",
    cellKind: "funnel_page_select",
    minW: "180px",
  },
  {
    id: "verification_page_url_display",
    groupId: "funnel_page",
    headerLabel: "実施するページURL",
    formikPath: "verificationPageUrl",
    cellKind: "readonly",
    minW: "160px",
  },
];

const popupGroupCoreColumns: PopupImportColumnDef[] = [
  {
    id: "popup_group_id",
    groupId: "popup_group",
    headerLabel: "ポップアップグループID",
    formikPath: "popupGroupId",
    cellKind: "text",
    minW: "120px",
  },
  {
    id: "popup_group_name",
    groupId: "popup_group",
    headerLabel: "ポップアップグループ名",
    formikPath: "popupGroupName",
    cellKind: "synced_group_name",
    minW: "140px",
  },
  {
    id: "memo",
    groupId: "popup_group",
    headerLabel: "メモ",
    formikPath: "group.memo",
    cellKind: "text",
    minW: "100px",
  },
  {
    id: "start_date",
    groupId: "popup_group",
    headerLabel: "実施開始日",
    formikPath: "group.startDate",
    cellKind: "formik_date",
    minW: "140px",
  },
  {
    id: "end_date",
    groupId: "popup_group",
    headerLabel: "実施終了日",
    formikPath: "group.endDate",
    cellKind: "formik_date",
    minW: "140px",
  },
  {
    id: "schedule_ids",
    groupId: "popup_group",
    headerLabel: "実施時刻",
    formikPath: "group.scheduleIds",
    cellKind: "schedule_list",
    minW: "280px",
  },
  {
    id: "deliver_day_of_weeks",
    groupId: "popup_group",
    headerLabel: "実施曜日",
    formikPath: "group.deliverDayOfWeeks",
    cellKind: "deliver_day_of_weeks",
    minW: "420px",
  },
  {
    id: "redisplay_popup_time",
    groupId: "popup_group",
    headerLabel: "再表示禁止期間",
    formikPath: "group.redisplayPopupTime",
    cellKind: "text",
    minW: "88px",
  },
  {
    id: "distribution_type",
    groupId: "popup_group",
    headerLabel: "配信タイプ",
    formikPath: "group.isMulti",
    cellKind: "distribution_type_select",
    minW: "160px",
  },
  {
    id: "deliver_kind",
    groupId: "popup_group",
    headerLabel: "配信方法",
    formikPath: "group.deliverKind",
    cellKind: "deliver_kind_select",
    minW: "200px",
  },
  {
    id: "weight",
    groupId: "popup_group",
    headerLabel: "配信比率",
    formikPath: "group.weightRaw",
    cellKind: "text",
    minW: "72px",
  },
  {
    id: "content_kind",
    groupId: "popup_group",
    headerLabel: "ポップアップの種類",
    formikPath: "group.contentKindRaw",
    cellKind: "content_kind_select",
    minW: "160px",
  },
];

const popupGroupFilterColumns: PopupImportColumnDef[] = [
  {
    id: "devices",
    groupId: "popup_group_filters",
    headerLabel: "デバイス",
    formikPath: "group.devices",
    cellKind: "graphql_enum_multi",
    graphqlEnumMultiKey: "Device",
    minW: "140px",
  },
  {
    id: "platforms",
    groupId: "popup_group_filters",
    headerLabel: "プラットフォーム",
    formikPath: "group.platforms",
    cellKind: "graphql_enum_multi",
    graphqlEnumMultiKey: "Platform",
    minW: "160px",
  },
  {
    id: "browsers",
    groupId: "popup_group_filters",
    headerLabel: "ブラウザ",
    formikPath: "group.browsers",
    cellKind: "graphql_enum_multi",
    graphqlEnumMultiKey: "Browser",
    minW: "140px",
  },
  {
    id: "genders",
    groupId: "popup_group_filters",
    headerLabel: "性別",
    formikPath: "group.genders",
    cellKind: "graphql_enum_multi",
    graphqlEnumMultiKey: "Gender",
    minW: "120px",
  },
  {
    id: "ages",
    groupId: "popup_group_filters",
    headerLabel: "年齢",
    formikPath: "group.ageRanges",
    cellKind: "graphql_enum_multi",
    graphqlEnumMultiKey: "AgeRange",
    minW: "200px",
  },
  {
    id: "incomes",
    groupId: "popup_group_filters",
    headerLabel: "年収",
    formikPath: "group.annualIncomes",
    cellKind: "graphql_enum_multi",
    graphqlEnumMultiKey: "AnnualIncome",
    minW: "200px",
  },
  {
    id: "source_ids",
    groupId: "popup_group_filters",
    headerLabel: "表示条件に流入元カテゴリを追加する",
    formikPath: "group.sourceIds",
    cellKind: "source_id_list",
    minW: "300px",
  },
  {
    id: "exclude_source_ids",
    groupId: "popup_group_filters",
    headerLabel: "非表示条件に流入元カテゴリを追加する",
    formikPath: "group.excludeSourceIds",
    cellKind: "source_id_list",
    minW: "300px",
  },
  {
    id: "referrers",
    groupId: "popup_group_filters",
    headerLabel: "表示条件に流入元を追加する",
    formikPath: "group.referrers",
    cellKind: "referrer_list",
    minW: "140px",
  },
  {
    id: "exclude_referrers",
    groupId: "popup_group_filters",
    headerLabel: "表示条件に除外流入元を追加する",
    formikPath: "group.excludeReferrers",
    cellKind: "exclude_referrer_list",
    minW: "140px",
  },
  {
    id: "visit_count",
    groupId: "popup_group_filters",
    headerLabel: "表示条件にユーザーの訪問回数を追加",
    formikPath: "group.visitCount",
    cellKind: "text",
    minW: "88px",
  },
  {
    id: "display_targets",
    groupId: "popup_group_filters",
    headerLabel: POPUP_GROUP_DISTRIBUTION_LABELS.displayTargetContents,
    formikPath: "group.displayTargetContents",
    cellKind: "json_array",
    minW: "160px",
  },
  {
    id: "disable_targets",
    groupId: "popup_group_filters",
    headerLabel: POPUP_GROUP_DISTRIBUTION_LABELS.disableTargetElements,
    formikPath: "group.disableTargetElements",
    cellKind: "json_array",
    minW: "160px",
  },
  {
    id: "deliver_target_urls",
    groupId: "popup_group_filters",
    headerLabel: POPUP_GROUP_DISTRIBUTION_LABELS.deliverTargetUrls,
    formikPath: "group.deliverTargetUrls",
    cellKind: "url_list",
    minW: "160px",
  },
  {
    id: "deliver_exclude_urls",
    groupId: "popup_group_filters",
    headerLabel: POPUP_GROUP_DISTRIBUTION_LABELS.deliverExcludeUrls,
    formikPath: "group.deliverExcludeUrls",
    cellKind: "url_list",
    minW: "160px",
  },
  {
    id: "cookies",
    groupId: "popup_group_filters",
    headerLabel: POPUP_GROUP_DISTRIBUTION_LABELS.targetCookies,
    formikPath: "group.targetCookies",
    cellKind: "cookies_cell",
    minW: "160px",
  },
];

const popupColumns: PopupImportColumnDef[] = [
  {
    id: "popup_name",
    groupId: "popup",
    headerLabel: "ポップアップ名",
    formikPath: "popup.name",
    cellKind: "text",
    minW: "140px",
  },
  {
    id: "close_btn",
    groupId: "popup",
    headerLabel: "閉じるボタンの表示",
    formikPath: "popup.isClose",
    cellKind: "switch_bool",
    minW: "88px",
  },
  {
    id: "content_width",
    groupId: "popup",
    headerLabel: "ポップアップの幅",
    formikPath: "popup.contentWidth",
    cellKind: "text",
    minW: "72px",
  },
  {
    id: "sp_content_width",
    groupId: "popup",
    headerLabel: "スマートフォンポップアップの幅",
    formikPath: "popup.spContentWidth",
    cellKind: "text",
    minW: "88px",
  },
  {
    id: "color_kind",
    groupId: "popup",
    headerLabel: "配色デザイン",
    formikPath: "popup.colorKindRaw",
    cellKind: "enum_select",
    selectOptions: GraphQLEnums.PopupColorKind,
    minW: "120px",
  },
  {
    id: "title",
    groupId: "popup",
    headerLabel: "タイトル",
    formikPath: "popup.title",
    cellKind: "text",
    minW: "100px",
  },
  {
    id: "description",
    groupId: "popup",
    headerLabel: "説明文",
    formikPath: "popup.description",
    cellKind: "textarea",
    minW: "140px",
  },
  {
    id: "button",
    groupId: "popup",
    headerLabel: "ボタンテキスト",
    formikPath: "popup.button",
    cellKind: "text",
    minW: "100px",
  },
  {
    id: "url",
    groupId: "popup",
    headerLabel: "クリック時のURL",
    formikPath: "popup.url",
    cellKind: "text",
    minW: "140px",
  },
  {
    id: "inherit_query",
    groupId: "popup",
    headerLabel:
      "クエリパラメータを引き継ぐ（※ページの?以降の文字を引き継ぎます）",
    formikPath: "popup.isInheritQuery",
    cellKind: "csv_bool_switch",
    minW: "72px",
  },
  {
    id: "href_target",
    groupId: "popup",
    headerLabel: "クリック時のタブの扱い",
    formikPath: "popup.hrefTargetRaw",
    cellKind: "enum_select",
    selectOptions: GraphQLEnums.PopupHrefTarget,
    minW: "140px",
  },
  {
    id: "action_kind",
    groupId: "popup",
    headerLabel: "表示タイミング",
    formikPath: "popup.actionKindRaw",
    cellKind: "action_timing_block",
    minW: "200px",
  },
  {
    id: "display_on_tab",
    groupId: "popup",
    headerLabel: "タブの切り替えで表示",
    formikPath: "popup.displayOnTabFocus",
    cellKind: "csv_bool_switch",
    minW: "88px",
  },
  {
    id: "display_on_mouseout",
    groupId: "popup",
    headerLabel: "ポインタが外れた際にポップアップを表示",
    formikPath: "popup.displayOnMouseOut",
    cellKind: "csv_bool_switch",
    minW: "88px",
  },
  {
    id: "display_no_op",
    groupId: "popup",
    headerLabel: "15秒ユーザー操作がない場合ポップアップを表示",
    formikPath: "popup.isDisplayWhenNoOperation",
    cellKind: "csv_bool_switch",
    minW: "88px",
  },
  {
    id: "display_kind",
    groupId: "popup",
    headerLabel: "表示場所",
    formikPath: "popup.displayKindRaw",
    cellKind: "enum_select",
    selectOptions: GraphQLEnums.PopupDisplayKind,
    minW: "140px",
  },
  {
    id: "enable_tap",
    groupId: "popup",
    headerLabel: "背景の操作",
    formikPath: "popup.enableTapWindow",
    cellKind: "csv_bool_switch",
    minW: "88px",
  },
  {
    id: "close_outside",
    groupId: "popup",
    headerLabel: "背景をクリック時にポップアップを非表示にする",
    formikPath: "popup.closePopupByClickingOutsideRaw",
    cellKind: "enum_select",
    selectOptions: GraphQLEnums.PopupClosePopupByClickingOutside,
    minW: "140px",
  },
  {
    id: "bg_opacity",
    groupId: "popup",
    headerLabel: "背景濃度",
    formikPath: "popup.backgroundOpacity",
    cellKind: "text",
    minW: "72px",
  },
  {
    id: "hide_scroll",
    groupId: "popup",
    headerLabel: "スクロール率制限",
    formikPath: "popup.hideByScrollPercentageRaw",
    cellKind: "text",
    minW: "88px",
  },
  {
    id: "hide_onfocus",
    groupId: "popup",
    headerLabel: "入力中にポップアップを表示させない",
    formikPath: "popup.hideByOnfocus",
    cellKind: "csv_bool_switch",
    minW: "88px",
  },
  {
    id: "quit_timing",
    groupId: "popup",
    headerLabel: "表示時間",
    formikPath: "popup.quitTiming",
    cellKind: "text",
    minW: "72px",
  },
  {
    id: "display_count",
    groupId: "popup",
    headerLabel: "表示回数を制限",
    formikPath: "popup.displayCountLimit",
    cellKind: "text",
    minW: "88px",
  },
  {
    id: "display_again",
    groupId: "popup",
    headerLabel: "再表示ボタン",
    formikPath: "popup.displayPopupAgain",
    cellKind: "enum_select",
    selectOptions: GraphQLEnums.DistributionStatus,
    minW: "100px",
  },
  {
    id: "again_img",
    groupId: "popup",
    headerLabel: "再表示ボタン用画像",
    formikPath: "popup.displayPopupAgainImage",
    cellKind: "display_popup_again_image",
    minW: "200px",
  },
  {
    id: "again_msg",
    groupId: "popup",
    headerLabel: "再表示ボタン用メッセージ",
    formikPath: "popup.displayPopupAgainMessage",
    cellKind: "text",
    minW: "120px",
  },
  {
    id: "animation",
    groupId: "popup",
    headerLabel: "アニメーション",
    formikPath: "popup.animationKindRaw",
    cellKind: "enum_select",
    selectOptions: GraphQLEnums.PopupAnimationKind,
    minW: "120px",
  },
  {
    id: "yda_code",
    groupId: "popup",
    headerLabel: "YDAコード",
    formikPath: "popup.honeycombCode",
    cellKind: "text",
    minW: "88px",
  },
  {
    id: "yda_trigger",
    groupId: "popup",
    headerLabel: "YDAコードトリガー",
    formikPath: "popup.ydaTriggerRaw",
    cellKind: "enum_select",
    selectOptions: popupImportYdaTriggerSelectOptions,
    minW: "160px",
  },
];

export const POPUP_IMPORT_COLUMNS: PopupImportColumnDef[] = [
  ...rowLeadColumns,
  ...funnelPageColumns,
  ...popupGroupCoreColumns,
  ...popupGroupFilterColumns,
  ...popupColumns,
  ...imageColumns(),
];

export type PopupImportGroupVisibility = Partial<
  Record<PopupImportColumnGroupId, boolean>
>;

export const POPUP_IMPORT_DEFAULT_GROUP_VISIBILITY: Record<
  PopupImportColumnGroupId,
  boolean
> = {
  row_lead: true,
  funnel_page: true,
  popup_group: true,
  popup: true,
  popup_images: true,
  popup_group_filters: true,
};

export function getVisibleColumns(
  visibility: PopupImportGroupVisibility
): PopupImportColumnDef[] {
  const lead = POPUP_IMPORT_COLUMNS.filter((c) => c.groupId === "row_lead");
  const rest = POPUP_IMPORT_COLUMNS.filter(
    (c) => c.groupId !== "row_lead" && visibility[c.groupId] !== false
  );
  if (rest.length === 0) {
    return POPUP_IMPORT_COLUMNS;
  }
  return [...lead, ...rest];
}

export function getGroupHeaderSpans(
  cols: PopupImportColumnDef[]
): { groupId: PopupImportColumnGroupId; count: number }[] {
  const spans: { groupId: PopupImportColumnGroupId; count: number }[] = [];
  for (const c of cols) {
    const tail = spans[spans.length - 1];
    if (tail && tail.groupId === c.groupId) tail.count++;
    else spans.push({ groupId: c.groupId, count: 1 });
  }
  return spans;
}

/** Keep only `popup_images` columns for zero-based slots `< visibleSlotCount`. */
export function filterColumnsByImageSlotVisibility(
  cols: PopupImportColumnDef[],
  visibleSlotCount: number
): PopupImportColumnDef[] {
  return cols.filter((c) => {
    if (c.groupId !== "popup_images") return true;
    if (c.imageSlot === undefined) return true;
    return c.imageSlot < visibleSlotCount;
  });
}
